import {
  CUSTOMER_PDF_CELEBRITY_DISPLAY_LIMIT,
  type CustomerPdfAward,
  type CustomerPdfCelebrity,
  type CustomerPdfStat,
} from '@utazzo/shared';
import {
  UzBadge,
  UzButton,
  UzCard,
  UzConfirmDialog,
  UzEmptyState,
  UzSkeleton,
  UzTabs,
} from '@utazzo/ui';
import { Award, ImageIcon, Pencil, Plus, Star, Trash2, Users } from 'lucide-react';
import React from 'react';

import { AwardFormDrawer } from '../components/AwardFormDrawer';
import { CelebrityFormDrawer } from '../components/CelebrityFormDrawer';
import { CustomerPdfPreview } from '../components/CustomerPdfPreview';
import { StatFormDrawer } from '../components/StatFormDrawer';
import {
  useAwardsList,
  useCelebritiesList,
  useDeleteAward,
  useDeleteCelebrity,
  useDeleteStat,
  usePartnerLogosList,
  useSetPartnerLogos,
  useStatsList,
} from '../use-customer-pdf';

import { PageShell } from '@/components/page-shell';
import { useCurrentUser } from '@/features/auth/use-auth';
import { resolveMedia } from '@/features/website-cms/components/media.api';
import { MediaGalleryField } from '@/features/website-cms/components/MediaGalleryField';

const WRITE_ROLES = ['ADMIN', 'WEBSITE_MANAGER', 'WEBSITE_SEO', 'MARKETING_LEAD'];

/** Resolves a media id → preview URL and renders a thumbnail. */
const MediaThumb = ({
  id,
  className,
}: {
  id: string | null;
  className: string;
}): React.JSX.Element => {
  const [url, setUrl] = React.useState<string | null>(null);
  React.useEffect(() => {
    let cancelled = false;
    if (!id) {
      setUrl(null);
      return;
    }
    resolveMedia(id)
      .then((m) => !cancelled && setUrl(m.url))
      .catch(() => !cancelled && setUrl(null));
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (!id || !url) {
    return (
      <div className={`flex items-center justify-center bg-zinc-100 ${className}`}>
        <ImageIcon className="h-6 w-6 text-zinc-300" />
      </div>
    );
  }
  return <img src={url} alt="" className={`object-cover ${className}`} />;
};

// ---------------------------------------------------------------------------
// Tab 1 - "A Legacy of Trust & Excellence" (celebrities)
// ---------------------------------------------------------------------------

const LegacyTab = ({ canWrite }: { canWrite: boolean }): React.JSX.Element => {
  const { data: celebrities, isLoading } = useCelebritiesList();
  const deleteMut = useDeleteCelebrity();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<CustomerPdfCelebrity | null>(null);

  const openCreate = (): void => {
    setEditing(null);
    setDrawerOpen(true);
  };
  const openEdit = (c: CustomerPdfCelebrity): void => {
    setEditing(c);
    setDrawerOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-zinc-500">
          Celebrity endorsements shown on the customer page. Upload as many as you like - only the
          first <span className="font-semibold">{CUSTOMER_PDF_CELEBRITY_DISPLAY_LIMIT}</span> by
          sort order are shown.
        </p>
        {canWrite && (
          <UzButton onClick={openCreate}>
            <Plus className="mr-1.5 h-4 w-4" /> Add celebrity
          </UzButton>
        )}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }, (_, i) => (
            <UzSkeleton key={i} className="h-48 w-full rounded-xl" />
          ))}
        </div>
      ) : !celebrities?.length ? (
        <UzEmptyState
          title="No celebrities yet"
          description='Add celebrity endorsements for the "A Legacy of Trust & Excellence" section.'
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {celebrities.map((c, index) => (
            <UzCard key={c.id} className="relative p-4">
              <div className="flex items-start gap-3">
                <MediaThumb id={c.imageId} className="h-16 w-16 shrink-0 rounded-full" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-semibold text-zinc-900">{c.name}</span>
                    {index < CUSTOMER_PDF_CELEBRITY_DISPLAY_LIMIT ? (
                      <UzBadge tone="success">Visible</UzBadge>
                    ) : (
                      <UzBadge tone="secondary">Hidden</UzBadge>
                    )}
                  </div>
                  <p className="mt-1 line-clamp-3 text-xs text-zinc-500">{c.description}</p>
                  <p className="mt-2 text-xs text-zinc-400">Sort order: {c.sortOrder}</p>
                </div>
              </div>
              {canWrite && (
                <div className="mt-3 flex justify-end gap-1 border-t border-zinc-100 pt-3">
                  <UzButton variant="ghost" size="sm" onClick={() => openEdit(c)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </UzButton>
                  <UzConfirmDialog
                    trigger={
                      <UzButton variant="ghost" size="sm">
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </UzButton>
                    }
                    title={`Remove ${c.name}?`}
                    description="This removes the celebrity from the customer page immediately."
                    confirmLabel="Remove"
                    tone="destructive"
                    onConfirm={() => deleteMut.mutateAsync(c.id).then(() => undefined)}
                  />
                </div>
              )}
            </UzCard>
          ))}
        </div>
      )}

      <CelebrityFormDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        mode={editing ? 'edit' : 'create'}
        celebrity={editing}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 2 - "Awards & Accolades"
// ---------------------------------------------------------------------------

const AwardsTab = ({ canWrite }: { canWrite: boolean }): React.JSX.Element => {
  const { data: awards, isLoading: awardsLoading } = useAwardsList();
  const deleteMut = useDeleteAward();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<CustomerPdfAward | null>(null);

  const openCreate = (): void => {
    setEditing(null);
    setDrawerOpen(true);
  };
  const openEdit = (a: CustomerPdfAward): void => {
    setEditing(a);
    setDrawerOpen(true);
  };

  return (
    <div className="space-y-8">
      {/* ------------------------------ Awards ------------------------------ */}
      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Award className="h-4 w-4 text-zinc-400" />
            <h3 className="font-semibold text-zinc-900">Awards</h3>
          </div>
          {canWrite && (
            <UzButton onClick={openCreate}>
              <Plus className="mr-1.5 h-4 w-4" /> Add award
            </UzButton>
          )}
        </div>

        {awardsLoading ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {Array.from({ length: 5 }, (_, i) => (
              <UzSkeleton key={i} className="h-40 w-full rounded-xl" />
            ))}
          </div>
        ) : !awards?.length ? (
          <UzEmptyState
            title="No awards yet"
            description="Add award certificates with a title, e.g. 'Abu Dhabi Travel Awards - Valued Partner (2023)'."
          />
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {awards.map((a) => (
              <UzCard key={a.id} className="p-3">
                <MediaThumb id={a.imageId} className="h-24 w-full rounded-lg" />
                <p className="mt-2 line-clamp-2 text-center text-xs font-semibold text-zinc-900">
                  {a.title}
                </p>
                <p className="mt-1 text-center text-[11px] text-zinc-400">Sort: {a.sortOrder}</p>
                {canWrite && (
                  <div className="mt-2 flex justify-center gap-1 border-t border-zinc-100 pt-2">
                    <UzButton variant="ghost" size="sm" onClick={() => openEdit(a)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </UzButton>
                    <UzConfirmDialog
                      trigger={
                        <UzButton variant="ghost" size="sm">
                          <Trash2 className="h-3.5 w-3.5 text-red-500" />
                        </UzButton>
                      }
                      title="Remove this award?"
                      description="This removes the award from the customer page immediately."
                      confirmLabel="Remove"
                      tone="destructive"
                      onConfirm={() => deleteMut.mutateAsync(a.id).then(() => undefined)}
                    />
                  </div>
                )}
              </UzCard>
            ))}
          </div>
        )}
      </section>

      <AwardFormDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        mode={editing ? 'edit' : 'create'}
        award={editing}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 3 - "Destination Partners" (the logo wall under its own heading on the
// customer page). Upload adds, drag reorders, remove deletes - "Save" persists
// the whole wall in one call.
// ---------------------------------------------------------------------------

const DestinationPartnersTab = ({ canWrite }: { canWrite: boolean }): React.JSX.Element => {
  const { data: partnerLogos, isLoading: logosLoading } = usePartnerLogosList();
  const setLogosMut = useSetPartnerLogos();
  const [logoIds, setLogoIds] = React.useState<string[]>([]);
  const [logosDirty, setLogosDirty] = React.useState(false);

  // Seed local gallery state from the server once (and after saves).
  React.useEffect(() => {
    if (partnerLogos && !logosDirty) {
      setLogoIds(partnerLogos.map((p) => p.imageId));
    }
  }, [partnerLogos, logosDirty]);

  const saveLogos = async (): Promise<void> => {
    try {
      await setLogosMut.mutateAsync(logoIds);
      setLogosDirty(false);
    } catch {
      /* toast already fired */
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-zinc-400" />
          <h3 className="font-semibold text-zinc-900">Destination Partners</h3>
        </div>
        {canWrite && (
          <UzButton onClick={saveLogos} loading={setLogosMut.isPending} disabled={!logosDirty}>
            Save destination partners
          </UzButton>
        )}
      </div>
      <p className="text-sm text-zinc-500">
        Tourism boards, airlines and partner brands. Upload to add, drag to reorder, remove to
        delete - they render under the &quot;Destination Partners&quot; heading on the customer page
        in this exact order.
      </p>
      {logosLoading ? (
        <UzSkeleton className="h-32 w-full rounded-xl" />
      ) : (
        <MediaGalleryField
          value={logoIds}
          onChange={(ids) => {
            setLogoIds(ids);
            setLogosDirty(true);
          }}
          folder="customer-pdf"
          disabled={!canWrite}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 4 - Stats strip
// ---------------------------------------------------------------------------

const StatsTab = ({ canWrite }: { canWrite: boolean }): React.JSX.Element => {
  const { data: stats, isLoading } = useStatsList();
  const deleteMut = useDeleteStat();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<CustomerPdfStat | null>(null);

  const openCreate = (): void => {
    setEditing(null);
    setDrawerOpen(true);
  };
  const openEdit = (s: CustomerPdfStat): void => {
    setEditing(s);
    setDrawerOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-zinc-500">
          Highlight tiles at the bottom of the Awards &amp; Partners section, e.g. &quot;100K+ Happy
          travellers&quot; or &quot;4.4 Rated&quot; with gold stars.
        </p>
        {canWrite && (
          <UzButton onClick={openCreate}>
            <Plus className="mr-1.5 h-4 w-4" /> Add stat
          </UzButton>
        )}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }, (_, i) => (
            <UzSkeleton key={i} className="h-28 w-full rounded-xl" />
          ))}
        </div>
      ) : !stats?.length ? (
        <UzEmptyState
          title="No stats yet"
          description='Add tiles like "100K+ Happy travellers", "15+ Years of experience" or rating badges.'
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((s) => (
            <UzCard key={s.id} className="p-4">
              <div className="flex items-center gap-3">
                <MediaThumb id={s.iconImageId} className="h-10 w-10 shrink-0 rounded-lg" />
                <div className="min-w-0 flex-1 leading-tight">
                  <div className="truncate font-semibold text-zinc-900">{s.value}</div>
                  {s.label && <div className="truncate text-xs text-zinc-500">{s.label}</div>}
                  {s.rating !== null && (
                    <div className="mt-0.5 flex items-center gap-1 text-xs text-amber-500">
                      <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                      {s.rating}
                    </div>
                  )}
                </div>
              </div>
              <p className="mt-2 text-xs text-zinc-400">Sort order: {s.sortOrder}</p>
              {canWrite && (
                <div className="mt-2 flex justify-end gap-1 border-t border-zinc-100 pt-2">
                  <UzButton variant="ghost" size="sm" onClick={() => openEdit(s)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </UzButton>
                  <UzConfirmDialog
                    trigger={
                      <UzButton variant="ghost" size="sm">
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </UzButton>
                    }
                    title={`Remove "${s.value}"?`}
                    description="This removes the stat tile from the customer page immediately."
                    confirmLabel="Remove"
                    tone="destructive"
                    onConfirm={() => deleteMut.mutateAsync(s.id).then(() => undefined)}
                  />
                </div>
              )}
            </UzCard>
          ))}
        </div>
      )}

      <StatFormDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        mode={editing ? 'edit' : 'create'}
        stat={editing}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const CustomerPdfPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canWrite = WRITE_ROLES.includes(me?.role ?? '');

  const tabs = [
    {
      value: 'legacy',
      label: 'Legacy of Trust',
      content: <LegacyTab canWrite={canWrite} />,
    },
    {
      value: 'awards',
      label: 'Awards & Accolades',
      content: <AwardsTab canWrite={canWrite} />,
    },
    {
      value: 'destination-partners',
      label: 'Destination Partners',
      content: <DestinationPartnersTab canWrite={canWrite} />,
    },
    {
      value: 'stats',
      label: 'Stats',
      content: <StatsTab canWrite={canWrite} />,
    },
    {
      value: 'preview',
      label: 'Preview',
      content: <CustomerPdfPreview />,
    },
  ];

  return (
    <PageShell
      title="Customer Itinerary Link"
      description='Static content for the customer itinerary page: "A Legacy of Trust & Excellence", "Awards & Accolades" and "Destination Partners".'
    >
      {/* Phones: the strip scrolls sideways instead of running off-screen. */}
      <UzTabs
        tabs={tabs}
        listClassName="max-sm:max-w-full max-sm:justify-start max-sm:overflow-x-auto"
      />
    </PageShell>
  );
};
